import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import CalendarPicker from '../util/CalendarPicker';
import { useSnackbar } from 'notistack';
import { t } from 'i18next';
import CloseIcon from '@mui/icons-material/Close';
import Select from 'react-select';
import { fxSelectStyles } from './AddExpenses';
import { transformToHierarchy } from '../util/tree';
import CategoryTaskPicker from '../timesheetsComp/CategoryTaskPicker';

interface Props {
  setShow: (v: boolean) => void;
  refreshExpenses: () => void;
  record: any;          // строка car-expense из таблицы (нужен record.id)
  unit?: any;
}

export default function EditCarExpenses({ setShow, refreshExpenses, record, unit }: Props) {
  const [startDate, setStartDate] = useState<Date>(new Date());
  const [purpose, setPurpose] = useState('');
  const [startPoint, setStartPoint] = useState('');
  const [destination, setDestination] = useState('');
  const [car, setCar] = useState('');
  const [distance, setDistance] = useState('');
  const [driver, setDriver] = useState('');
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState<number[]>([]);
  const [allCategory, setAllCategory] = useState<any[]>([]);
  const [drivableCars, setDrivableCars] = useState<any[]>([]);
  const [meId, setMeId] = useState<number | null>(null);
  const handleClose = () => setShow(false);
  const { enqueueSnackbar } = useSnackbar();

  const selectedCar = drivableCars.find((c: any) => String(c.id) === car);
  const driverOptions: any[] = selectedCar?.drivers || [];

  const handleCarChange = (val: string) => {
    setCar(val);
    const c = drivableCars.find((x: any) => String(x.id) === val);
    const opts: any[] = c?.drivers || [];
    if (opts.length && !opts.some((o) => String(o.id) === driver)) setDriver(String(opts[0].id));
  };

  const estimatedCost =
    selectedCar && distance && Number(distance) > 0
      ? (parseFloat(distance) * selectedCar.cost_per_km).toFixed(2)
      : null;

  useEffect(() => {
    (async () => {
      try {
        const { data: categoryData } = await axios.get('category');
        setAllCategory(categoryData || []);
        const { data: driv } = await axios.get('cars/drivable');
        setDrivableCars(Array.isArray(driv) ? driv : []);
        const { data: userData } = await axios.get('user');
        setMeId(userData?.id ?? null);
        // полная запись со всеми колонками
        const { data: rec } = await axios.get(`cars-expenses/${record.id}`);
        const src = rec || record;
        setStartDate(src.date ? new Date(src.date) : new Date());
        setPurpose(src.purpose || '');
        setStartPoint(src.starting_point || '');
        setDestination(src.destination || '');
        setCar(src.carsId != null ? String(src.carsId) : '');
        setDistance(src.distance != null ? String(src.distance) : '');
        setDriver(src.userId != null ? String(src.userId) : '');
        setAdditionalInfo(src.additional_info || '');
        if (src.categoryId != null) setSelectedCategoryId([src.categoryId]);
      } catch (e) {
        console.error(e);
      }
    })();
  }, [record]);

  const handleSave = async () => {
    if (selectedCategoryId.length === 0) { enqueueSnackbar(t('ChooseCategory'), { variant: 'error' }); return; }
    if (car === '') { enqueueSnackbar(t('ChooseCars'), { variant: 'error' }); return; }
    if (!startPoint.trim()) { enqueueSnackbar(t('StartingPointRequired'), { variant: 'error' }); return; }
    if (!destination.trim()) { enqueueSnackbar(t('DestinationRequired'), { variant: 'error' }); return; }
    if (!purpose.trim()) { enqueueSnackbar(t('PurposeRequired'), { variant: 'error' }); return; }
    if (!distance || Number(distance) <= 0) { enqueueSnackbar(t('DistanceRequired'), { variant: 'error' }); return; }

    try {
      await axios.put(`cars-expenses/${record.id}`, {
        date: startDate,
        categoryId: Number(selectedCategoryId[0]),
        carsId: Number(car),
        destination,
        starting_point: startPoint,
        purpose,
        distance: Number(distance),
        additional_info: additionalInfo,
        userId: driver ? Number(driver) : undefined,
      });
      handleClose();
      refreshExpenses();
      enqueueSnackbar(t('SavedSuccessfully'), { variant: 'success' });
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || 'Error updating', { variant: 'error' });
      console.error('Error updating:', error);
    }
  };

  const catTree = transformToHierarchy(allCategory);
  const carOptions = drivableCars.map((c: any) => ({ value: c.id, label: `${c.brand} ${c.model} (${c.plate_number})` }));
  const driverRsOptions = driverOptions.map((d: any) => ({ value: d.id, label: `${d.first_name} ${d.last_name}${meId === d.id ? ` (${t('You') || 'you'})` : ''}` }));

  return (
    <div className='fx-modal' role="presentation">
      {/* Header */}
      <div className='fx-head'>
        <div className='fx-head-text'>
          <div className='fx-head-title'>{t('AddCarExpenses')}</div>
          <div className='fx-head-sub'>{t('AddCarExpenseSub')}</div>
        </div>
        <div className='fx-close' onClick={handleClose}><CloseIcon fontSize="small" /></div>
      </div>

      {/* Body */}
      <div className='fx-body'>
        {/* Date + Purpose */}
        <div className='fx-row2'>
          <div className='fx-field'>
            <p className='fx-label'>{t('Date')}: <span className='text-red-400'>*</span></p>
            <CalendarPicker
              mode="single"
              variant="bare"
              className="fx-input"
              value={startDate}
              onChange={(date: any) => setStartDate(date)}
              displayFormat="DD/MM/YYYY"
            />
          </div>
          <div className='fx-field'>
            <p className='fx-label'>{t('Purpose')}: <span className='text-red-400'>*</span></p>
            <input type="text" value={purpose} title={t('Purpose')} className="fx-input"
              onChange={(e: any) => setPurpose(e.target.value)} />
          </div>
        </div>

        {/* Starting Point + Destination */}
        <div className='fx-row2'>
          <div className='fx-field'>
            <p className='fx-label'>{t('StartingPoint')}: <span className='text-red-400'>*</span></p>
            <input type="text" value={startPoint} title={t('StartingPoint')} className="fx-input"
              onChange={(e: any) => setStartPoint(e.target.value)} />
          </div>
          <div className='fx-field'>
            <p className='fx-label'>{t('Destination')}: <span className='text-red-400'>*</span></p>
            <input type="text" value={destination} title={t('Destination')} className="fx-input"
              onChange={(e: any) => setDestination(e.target.value)} />
          </div>
        </div>

        {/* Cars + Distance */}
        <div className='fx-row2'>
          <div className='fx-field'>
            <p className='fx-label'>{t('Cars')}: <span className='text-red-400'>*</span></p>
            <Select
              classNamePrefix="app-rs"
              menuShouldScrollIntoView={false}
              menuPlacement="auto"
              options={carOptions}
              value={carOptions.find((o: any) => String(o.value) === String(car)) || null}
              onChange={(v: any) => handleCarChange(v ? String(v.value) : '')}
              placeholder={t('SelectCars') || 'Select Cars'}
              styles={fxSelectStyles}
              menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
              isClearable
            />
          </div>
          <div className='fx-field'>
            <p className='fx-label'>{t('Distance')}: <span className='text-red-400'>*</span></p>
            <input type="text" value={distance} title={t('Distance')} className="fx-input"
              onChange={(e: any) => {
                let value = e.target.value;
                value = value.replace(/[^0-9.]/g, '');
                if ((value.match(/\./g) || []).length > 1) value = value.slice(0, -1);
                setDistance(value);
              }} />
          </div>
        </div>

        {/* Driver */}
        <div className='fx-field'>
          <p className='fx-label'>{t('Driver') || 'Driver'}: <span className='text-red-400'>*</span></p>
          <Select
            classNamePrefix="app-rs"
            menuShouldScrollIntoView={false}
            menuPlacement="auto"
            options={driverRsOptions}
            value={driverRsOptions.find((o: any) => String(o.value) === String(driver)) || null}
            onChange={(v: any) => setDriver(v ? String(v.value) : '')}
            isDisabled={!car}
            placeholder="—"
            styles={fxSelectStyles}
            menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
          />
        </div>

        {/* live estimated cost */}
        {estimatedCost !== null && (
          <p className='text-[13px] text-[var(--text-color-calendar-week)]'>
            {t('EstimatedCost')}: <span className='font-semibold text-[var(--text-color)]'>{estimatedCost} {unit || 'km'}</span>
          </p>
        )}

        {/* Category */}
        <div className='fx-field'>
          <p className='fx-label'>{t('Category')} <span className='fx-req'>*</span></p>
          <CategoryTaskPicker
            nodes={catTree}
            selectedCategoryId={selectedCategoryId}
            categoryName={[]}
            onCategorySelect={(id) => setSelectedCategoryId(id === -1 ? [] : [id])}
            selectedTasks={[]}
            setSelectedTasks={() => {}}
            taskOrTasks={false}
            categoryOnly
            block
          />
        </div>

        {/* Additional info */}
        <div className='fx-field'>
          <p className='fx-label'>{t('AdditionalInfo')}:</p>
          <textarea value={additionalInfo} title={t('AdditionalInfo')} className="fx-textarea" rows={2}
            onChange={(e: any) => setAdditionalInfo(e.target.value)} />
        </div>
      </div>

      {/* Footer */}
      <div className='fx-foot'>
        <button type="button" className='fx-save' onClick={handleSave}>{t('Save')}</button>
        <button type="button" className='fx-cancel' onClick={handleClose}>{t('Cancel')}</button>
      </div>
    </div>
  );
}
